import { useEffect, useRef } from 'react';
import { Checkbox } from '@/components/shadcn/checkbox.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import { Textarea } from '@/components/shadcn/textarea.tsx';
import {
  govtIntakeFormHowOftenUpdated,
  govtIntakeFormPropertyTypeManaged,
  govtIntakeFormTrackingMethodOptions,
} from '@/data/govtIntakeStaticData.ts';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import {
  EMPTY_PROJECT_TRACKING_DATA,
  type PropertyTrackingData,
  useIntakeForm,
} from '@/modules/governmentIntake/context/intake-form-context.tsx';

export default function PropertiesSubTrackingToday({ onValidationChange }: ValidationChange) {
  const { data, updatePropertiesSubSection } = useIntakeForm();
  const trackingData = data.properties?.tracking ?? EMPTY_PROJECT_TRACKING_DATA;

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useEffect(() => {
    onValidationChangeRef.current?.(true);
  }, []);

  const updateTracking = (newData: PropertyTrackingData) => {
    updatePropertiesSubSection('tracking', newData);
  };

  const handlePropertyTypeChange = (id: string, checked: boolean) => {
    updateTracking({
      ...trackingData,
      propertyType: { ...trackingData.propertyType, [id]: checked },
    });
  };

  const handlePropertyTrackingChange = (id: string, checked: boolean) => {
    updateTracking({
      ...trackingData,
      propertyTracking: { ...trackingData.propertyTracking, [id]: checked },
    });
  };

  const handlePropertyUpdatesChange = (id: string, checked: boolean) => {
    updateTracking({
      ...trackingData,
      propertyUpdates: { ...trackingData.propertyUpdates, [id]: checked },
    });
  };

  const handleVisibilityGapsChange = (value: string) => {
    updateTracking({
      ...trackingData,
      visibilityGaps: value,
    });
  };

  return (
    <div className='flex flex-col flex-1 justify-center'>
      <header className='shrink-0 pt-4'>
        <HeaderAndSub title='Property Tracking Today' subTitle='How properties are tracked and updated today.' />
      </header>

      <section className='flex-1 overflow-y-auto pb-4'>
        <div className='space-y-6 pt-8'>
          <div className='space-y-8'>
            {/* Property Types */}
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>Types of properties you manage</Label>
              {govtIntakeFormPropertyTypeManaged.map((option) => (
                <div key={`type-${option.id}`} className='flex items-center space-x-3'>
                  <Checkbox
                    id={`type-${option.id}`}
                    checked={Boolean(trackingData.propertyType[option.id])}
                    onCheckedChange={(checked) => handlePropertyTypeChange(option.id, Boolean(checked))}
                  />
                  <Label htmlFor={`type-${option.id}`} className='cursor-pointer select-none text-sm'>
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Tracking Methods */}
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>How properties are tracked today</Label>
              {govtIntakeFormTrackingMethodOptions.map((option) => (
                <div key={`tracking-${option.id}`} className='flex items-center space-x-3'>
                  <Checkbox
                    id={`tracking-${option.id}`}
                    checked={Boolean(trackingData.propertyTracking[option.id])}
                    onCheckedChange={(checked) => handlePropertyTrackingChange(option.id, Boolean(checked))}
                  />
                  <Label htmlFor={`tracking-${option.id}`} className='cursor-pointer select-none text-sm'>
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Update Frequency */}
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>How often is property data updated?</Label>
              {govtIntakeFormHowOftenUpdated.map((option) => (
                <div key={`updates-${option.id}`} className='flex items-center space-x-3'>
                  <Checkbox
                    id={`updates-${option.id}`}
                    checked={Boolean(trackingData.propertyUpdates[option.id])}
                    onCheckedChange={(checked) => handlePropertyUpdatesChange(option.id, Boolean(checked))}
                  />
                  <Label htmlFor={`updates-${option.id}`} className='cursor-pointer select-none text-sm'>
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Visibility Gaps */}
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>Visibility gaps across departments</Label>
              <Textarea
                value={trackingData.visibilityGaps}
                onChange={(e) => handleVisibilityGapsChange(e.target.value)}
                rows={4}
                placeholder='Describe what information is siloed or hard to access across teams...'
                className='resize-none'
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
